import { usePageSeo } from "@/hooks/usePageSeo";
import Navbar from "@/components/Navbar";
import ServicesSection from "@/components/ServicesSection";
import CTASection from "@/components/CTASection";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Plane, Building2, Users, CheckCircle, Compass } from "lucide-react";
import serviceAirport from "@/assets/service-airport.jpg";
import serviceHotel from "@/assets/service-hotel.jpg";
import serviceGroup from "@/assets/service-group.jpg";

const steps = [
  { step: "1", title: "Enter Locations", description: "Type your pick-up and drop-off points in the booking form." },
  { step: "2", title: "Choose Vehicle", description: "Select from our fleet based on your group size and preferences." },
  { step: "3", title: "Confirm & Ride", description: "Complete payment and your driver will be waiting for you." },
];

const ServicesPage = () => {
  usePageSeo("services");
  return (
    <main className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative py-32 overflow-hidden" style={{ background: `hsl(var(--hero-bg))` }}>
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-pattern-grid opacity-20" />
          <div className="absolute right-0 top-0 w-1/2 h-full">
            <img src="https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=1600&h=600&fit=crop" alt="AOT Transfers services" className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--hero-bg))] to-transparent" />
          </div>
          <div className="absolute top-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        </div>
        <Navbar />
        <div className="container mx-auto px-4 relative z-10 pt-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <p className="text-secondary font-sans font-semibold tracking-[0.2em] uppercase text-sm mb-3">What We Offer</p>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-hero-foreground mb-4">
              Our <span className="text-gradient-tropical">Services</span>
            </h1>
            <p className="text-hero-foreground/70 font-sans text-lg max-w-xl">
              Premium tours and transportation across the Dominican Republic — from airport transfers to unforgettable excursions.
            </p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-auto">
            <polygon points="0,80 1440,80 1440,40 720,0 0,40" className="fill-background" />
          </svg>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <p className="text-primary font-sans font-semibold tracking-[0.2em] uppercase text-sm mb-3">Simple Process</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-foreground">
              How It <span className="text-gradient-tropical">Works</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto relative">
            <div className="hidden md:block absolute top-16 left-1/6 right-1/6 h-0.5 bg-gradient-tropical opacity-30" />
            {steps.map((s, i) => (
              <motion.div key={s.step} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }} className="text-center relative">
                <div className="w-16 h-16 rounded-full bg-gradient-tropical text-primary-foreground flex items-center justify-center mx-auto mb-6 font-display font-extrabold text-2xl relative z-10 shadow-tropical">
                  {s.step}
                </div>
                <h3 className="font-display text-xl font-bold text-foreground mb-2">{s.title}</h3>
                <p className="text-muted-foreground font-sans text-sm leading-relaxed">{s.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container mx-auto px-4">
          {[
            { icon: Plane, title: "Airport Transfers", description: "Professional door-to-door service from all Dominican Republic airports. Your driver will greet you with a name sign and help with luggage.", image: serviceAirport, features: ["Meet & Greet at terminal", "Flight tracking", "Free waiting time", "Fixed prices"] },
            { icon: Building2, title: "Hotel to Hotel", description: "Comfortable inter-hotel transfers with fixed pricing. No surprises, no haggling.", image: serviceHotel, features: ["Best route calculation", "Fixed pricing", "Comfortable vehicles", "All destinations"], reverse: true },
            { icon: Users, title: "Group Transfers", description: "Spacious vans and minibuses for groups up to 15+ passengers with all luggage.", image: serviceGroup, features: ["Up to 15+ passengers", "Spacious vehicles", "Group discounts", "Luggage capacity"] },
            { icon: Compass, title: "Tours & Excursions", description: "Discover the best of DR with curated tours — from island hopping to city adventures and nature expeditions.", image: "https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=800&auto=format&fit=crop", features: ["Island excursions", "City tours", "Adventure activities", "Private tours"], reverse: true },
          ].map((service) => (
            <motion.div key={service.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className={`flex flex-col ${"reverse" in service && service.reverse ? "md:flex-row-reverse" : "md:flex-row"} items-center gap-12 mb-20 last:mb-0`}>
              <div className="md:w-1/2">
                <div className="relative overflow-hidden rounded-2xl">
                  <img src={service.image} alt={service.title} className="w-full h-80 object-cover" loading="lazy" />
                  <div className="absolute top-4 left-4 w-12 h-12 rounded-xl bg-gradient-tropical flex items-center justify-center shadow-tropical">
                    <service.icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                </div>
              </div>
              <div className="md:w-1/2">
                <h3 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">{service.title}</h3>
                <p className="text-muted-foreground font-sans leading-relaxed mb-6">{service.description}</p>
                <div className="grid grid-cols-2 gap-3">
                  {service.features.map((f) => (
                    <div key={f} className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-foreground font-sans text-sm">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <CTASection />
      <Footer />
    </main>
  );
};

export default ServicesPage;
